import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { RendererProps } from "../BaseRenderer";
import { ChoiceModel } from "../../../models/ChoiceModel";
import useStyle from "./NetPromoterItemRenderer.styles";
import useTranslatable from "../../../hooks/useTranslatable";
import useDimensionContext from "../../../hooks/useDimensionContext";
import { useKeyboardNavigation } from "../../../hooks/useKeyboardNavigation";
import useQuestion from "../../../hooks/useQuestion";
import { useMouseHover } from "../../../hooks/useMouseHover";
import { shouldUseModernTheme } from "../../../theme/navigationButtonThemeColors";
import { Theme } from "../../../types/Theme";
import { NetPromoterNavigation } from "../../controls/KeyboardNavigation/NetPromoterNavigation";

interface Props extends RendererProps {
    choice?: ChoiceModel;
    selected?: boolean;
    isDisabled?: boolean;
    onSelect?(choice: ChoiceModel): void;
    /** When set, combines scale value with NPS left/right label ids (first/last choices). */
    ariaLabelledBy?: string;
}

export default function NetPromoterItemRenderer(props: Props) {
    const { choice, selected, isDisabled, onSelect, variable, ariaLabelledBy } = props;
    const theme = useTheme() as Theme;
    const useModernTheme = shouldUseModernTheme(theme);
    const dimension = useDimensionContext();
    const classes = useStyle({ theme, selected, dimension } as any);
    const label = useTranslatable(choice, "Text");
    const [hover, pointerHandlers] = useMouseHover();
    const [focused, setFocused] = React.useState(false);

    const { elementReference, onKeyDown } = useKeyboardNavigation(
        choice.elementId,
        variable,
        useQuestion(),
        NetPromoterNavigation,
        () => {
            if (!isDisabled) {
                onSelect(choice);
            }
        },
    );

    return (
        <div
            role="radio"
            ref={elementReference}
            aria-checked={selected}
            aria-disabled={isDisabled || undefined}
            {...(ariaLabelledBy ? { "aria-labelledby": ariaLabelledBy } : {})}
            tabIndex={isDisabled ? -1 : 0}
            className={classnames(
                classes.choice,
                useModernTheme && classes.choiceModernFocus,
                dimension && dimension.maxWidth(480) && classes.dynamicChoice,
                !isDisabled && useModernTheme && hover && classes.choiceHover,
                !isDisabled && !useModernTheme && (hover || focused) && classes.choiceActive,
                selected && !(useModernTheme && isDisabled) && classes.choiceSelected,
                useModernTheme && isDisabled && classes.choiceDisabled,
            )}
            style={{}}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            {...(isDisabled ? {} : pointerHandlers)}
            onClick={isDisabled ? undefined : () => onSelect(choice)}
            onKeyDown={isDisabled ? undefined : onKeyDown}
        >
            <div className={classes.content}>
                <span id={`${choice.elementId}_NPS_VALUE`}>{label}</span>
            </div>
        </div>
    );
}
